import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import { CloseOutlined, LeftOutlined } from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import './index.css';

export interface FloatingActionItem {
  key: string;
  order: number;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}

interface FloatingActionsContextValue {
  registerAction: (action: FloatingActionItem) => () => void;
}

const FloatingActionsContext = createContext<FloatingActionsContextValue | null>(null);
const PC_FLOATING_ACTION_BREAKPOINT = 768;
export const PC_FLOATING_ACTIONS_CLOSED_KEY = 'imgcloud_pc_floating_actions_closed_v1';
export const MOBILE_FLOATING_ACTIONS_CLOSED_KEY = 'imgcloud_mobile_floating_actions_closed_v1';

const isPcViewport = () => {
  if (typeof window === 'undefined') {
    return true;
  }
  return window.innerWidth > PC_FLOATING_ACTION_BREAKPOINT;
};

const readFloatingActionsClosed = (storageKey: string) => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

const saveFloatingActionsClosed = (storageKey: string, closed: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (closed) {
      window.localStorage.setItem(storageKey, 'true');
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch {
    // Browsers can block localStorage in private modes; keep the in-memory state.
  }
};

const useIsPcViewport = () => {
  const [isPc, setIsPc] = useState(isPcViewport);

  useEffect(() => {
    const updateIsPc = () => setIsPc(isPcViewport());
    updateIsPc();
    window.addEventListener('resize', updateIsPc);

    return () => {
      window.removeEventListener('resize', updateIsPc);
    };
  }, []);

  return isPc;
};

interface FloatingActionStackProps {
  actions: FloatingActionItem[];
  isPc: boolean;
  closeLabel: string;
  onClose: () => void;
}

const FloatingActionStack: React.FC<FloatingActionStackProps> = ({ actions, isPc, closeLabel, onClose }) => {
  if (actions.length === 0) {
    return null;
  }

  return (
    <div
      className={`floating-actions-stack${isPc ? ' floating-actions-stack--pc' : ''}`}
      aria-label="Quick actions"
      data-testid="floating-actions-stack"
    >
      <button
        type="button"
        className="floating-actions-close"
        title={closeLabel}
        aria-label={closeLabel}
        data-testid="floating-actions-close"
        onClick={onClose}
      >
        <CloseOutlined />
      </button>
      {actions.map((action) => (
        <Button
          key={action.key}
          className="floating-actions-button"
          type="primary"
          shape="circle"
          size="large"
          icon={action.icon}
          title={action.label}
          aria-label={action.label}
          data-testid={action.testId}
          disabled={action.disabled}
          onClick={action.onClick}
        />
      ))}
    </div>
  );
};

interface FloatingActionsRestoreProps {
  label: string;
  onRestore: () => void;
}

const FloatingActionsRestore: React.FC<FloatingActionsRestoreProps> = ({ label, onRestore }) => (
  <button
    type="button"
    className="floating-actions-restore"
    title={label}
    aria-label={label}
    data-testid="floating-actions-restore"
    onClick={onRestore}
  >
    <LeftOutlined />
  </button>
);

export const FloatingActionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [actions, setActions] = useState<Record<string, FloatingActionItem>>({});
  const [pcActionsClosed, setPcActionsClosed] = useState(
    () => readFloatingActionsClosed(PC_FLOATING_ACTIONS_CLOSED_KEY)
  );
  const [mobileActionsClosed, setMobileActionsClosed] = useState(
    () => readFloatingActionsClosed(MOBILE_FLOATING_ACTIONS_CLOSED_KEY)
  );
  const isPc = useIsPcViewport();
  const actionsClosed = isPc ? pcActionsClosed : mobileActionsClosed;
  const setActionsClosed = isPc ? setPcActionsClosed : setMobileActionsClosed;
  const closedStateKey = isPc ? PC_FLOATING_ACTIONS_CLOSED_KEY : MOBILE_FLOATING_ACTIONS_CLOSED_KEY;

  const registerAction = useCallback((action: FloatingActionItem) => {
    setActions((prev) => ({ ...prev, [action.key]: action }));
    return () => {
      setActions((prev) => {
        if (!prev[action.key]) {
          return prev;
        }
        const next = { ...prev };
        delete next[action.key];
        return next;
      });
    };
  }, []);

  const closeActions = useCallback(() => {
    setActionsClosed(true);
    saveFloatingActionsClosed(closedStateKey, true);
  }, [setActionsClosed, closedStateKey]);

  const restoreActions = useCallback(() => {
    setActionsClosed(false);
    saveFloatingActionsClosed(closedStateKey, false);
  }, [setActionsClosed, closedStateKey]);

  const contextValue = useMemo(() => ({ registerAction }), [registerAction]);
  const orderedActions = useMemo(
    () => Object.values(actions).sort((a, b) => a.order - b.order),
    [actions]
  );
  const shouldShowRestore = actionsClosed && orderedActions.length > 0;

  return (
    <FloatingActionsContext.Provider value={contextValue}>
      {children}
      {shouldShowRestore ? (
        <FloatingActionsRestore label={t('floatingActions.restoreStack')} onRestore={restoreActions} />
      ) : (
        <FloatingActionStack
          actions={actionsClosed ? [] : orderedActions}
          isPc={isPc}
          closeLabel={t('floatingActions.closeStack')}
          onClose={closeActions}
        />
      )}
    </FloatingActionsContext.Provider>
  );
};

export const useFloatingAction = (action: FloatingActionItem | null) => {
  const context = useContext(FloatingActionsContext);

  useEffect(() => {
    if (!context || !action) {
      return undefined;
    }
    return context.registerAction(action);
  }, [action, context]);
};
